/**
 * Componentes visuais compartilhados pelas telas do módulo Financeiro.
 * Seguem o mesmo padrão visual de AccountingReportsView/BillingHistoryView.
 */
import React from 'react';
import { AlertCircle, RefreshCw, X, CheckCircle2 } from 'lucide-react';
import type { CategoryOptionGroup } from './financialUtils';

// Classes padrão de formulário (iguais às usadas em BillingHistoryView)
export const selectCls =
  'block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-emerald-500 disabled:bg-slate-100 disabled:text-slate-400';
export const inputCls =
  'block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-emerald-500 disabled:bg-slate-100 disabled:text-slate-400';
export const labelCls = 'block text-sm font-medium text-slate-700 mb-1';
export const primaryBtnCls =
  'inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors';
export const secondaryBtnCls =
  'inline-flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors';

/** Spinner de carregamento centralizado (padrão BillingHistoryView). */
export const LoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center py-16">
    <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

/** Caixa de erro vermelha padrão. */
export const ErrorBox: React.FC<{ message: string; onRetry?: () => void }> = ({ message, onRetry }) => (
  <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
    <AlertCircle size={16} className="shrink-0" />
    <span>{message}</span>
    {onRetry && (
      <button className="ml-auto text-red-500 hover:text-red-700" onClick={onRetry} title="Tentar novamente">
        <RefreshCw size={16} />
      </button>
    )}
  </div>
);

/** Aviso de sucesso (toast inline) verde, dispensável. */
export const SuccessBox: React.FC<{ message: string; onDismiss: () => void }> = ({ message, onDismiss }) => (
  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl p-4 text-sm">
    <CheckCircle2 size={16} className="shrink-0" />
    <span>{message}</span>
    <button className="ml-auto text-emerald-500 hover:text-emerald-700" onClick={onDismiss} title="Fechar">
      <X size={16} />
    </button>
  </div>
);

/** Fallback exibido quando o usuário não possui a permissão de leitura da tela. */
export const PermissionFallback: React.FC<{ label: string }> = ({ label }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 text-center">
    <div className="text-6xl text-slate-300 mb-4">🔒</div>
    <h2 className="text-xl font-semibold text-slate-700 mb-2">Acesso Restrito</h2>
    <p className="text-slate-500">Você não tem permissão para visualizar {label}.</p>
  </div>
);

/** Card de resumo (saldo inicial, entradas, saídas, etc.). */
export const SummaryCard: React.FC<{
  label: string;
  value: string;
  valueClassName?: string;
  /** Nota curta sob o valor — use para explicar um "—" em vez do número. */
  hint?: string;
}> = ({ label, value, valueClassName, hint }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
    <p className="text-sm text-slate-500">{label}</p>
    <p className={`text-lg sm:text-xl font-bold mt-1 break-words ${valueClassName ?? 'text-slate-900'}`}>{value}</p>
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

/** Estrutura padrão de modal (backdrop + card branco arredondado). */
export const ModalShell: React.FC<{
  title: string;
  icon?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
  maxWidth?: string;
}> = ({ title, icon, onClose, children, footer, maxWidth = 'max-w-lg' }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
    onMouseDown={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}
  >
    <div className={`bg-white rounded-2xl shadow-2xl w-full ${maxWidth} mx-4 flex flex-col max-h-[90vh]`}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
        <div className="flex items-center gap-2">
          {icon}
          <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
        >
          <X size={18} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">{children}</div>
      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
        {footer}
      </div>
    </div>
  </div>
);

/** Select de Categoria em dois níveis (pai como optgroup → filhos). */
export const CategorySelect: React.FC<{
  groups: CategoryOptionGroup[];
  value: number | null;
  onChange: (erpId: number | null) => void;
  disabled?: boolean;
  allowEmpty?: boolean;
  emptyLabel?: string;
}> = ({ groups, value, onChange, disabled, allowEmpty = true, emptyLabel = '— Selecione —' }) => (
  <select
    className={selectCls}
    value={value ?? ''}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
  >
    {allowEmpty && <option value="">{emptyLabel}</option>}
    {groups.map((group) => (
      <optgroup key={group.label} label={group.label}>
        {group.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </optgroup>
    ))}
  </select>
);
